import { useState, useEffect } from 'react';
import {
    Box,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import { useGetIdentity, usePermissions, useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import tokenManager from '../tokenManager';

interface RbacRole {
    id: number;
    description: string;
    rbac: object[];
}

const RoleSwitcher = () => {
    const dataProvider = useDataProvider();
    const { data: identity, refetch: refetchIdentity } = useGetIdentity();
    const { refetch: refetchPermissions } = usePermissions();
    const [activeRole, setActiveRole] = useState<number>(0);

    const { data: roles } = useQuery<RbacRole[]>({
        queryKey: ['rbac-roles'],
        queryFn: async () => {
            const { data } = await (dataProvider as any).getAny(
                'extralist/userapplication/rbac-roles',
                ''
            );
            return data;
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!identity?.userrights,
    });

    const realUserrights =
        identity?._originalUserrights ?? identity?.userrights ?? 0;

    useEffect(() => {
        if (identity?.userrights && activeRole === 0) {
            setActiveRole(identity.userrights);
        }
    }, [identity, activeRole]);

    if (realUserrights < 400 || !roles?.length) return null;

    const handleChange = (event: SelectChangeEvent<number>) => {
        const roleId = Number(event.target.value);
        const role = roles.find(r => r.id === roleId);
        if (!role) return;
        tokenManager.overrideRole(roleId, role.rbac);
        setActiveRole(roleId);
        refetchIdentity();
        refetchPermissions();
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
                variant="caption"
                sx={{ color: 'inherit', opacity: 0.8, whiteSpace: 'nowrap' }}
            >
                Rooli:
            </Typography>
            <Select
                value={activeRole || ''}
                onChange={handleChange}
                size="small"
                variant="outlined"
                sx={{
                    color: 'inherit',
                    fontSize: '0.8rem',
                    height: 30,
                    minWidth: 160,
                    '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.4)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.7)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.9)',
                    },
                    '.MuiSvgIcon-root': {
                        color: 'inherit',
                    },
                }}
            >
                {roles.map(role => (
                    <MenuItem key={role.id} value={role.id}>
                        {role.description} ({role.id})
                    </MenuItem>
                ))}
            </Select>
        </Box>
    );
};

export default RoleSwitcher;
